/**
 * Browser-side call to the Stage 0 shell worker (task 3.1).
 *
 * Shell generation used to be a chat turn: ~100 tools, ~52KB of prompt, no
 * output contract, `ready_to_answer` as an escape hatch. Measured 2026-08-31
 * it burned 8m10s and 33,959 output tokens to produce zero text. The workers
 * doing the same class of job with a narrow brief produced 16,822 chars of
 * valid ODM in 88s. This is that job, called as a job.
 *
 * Client-safe: imports only `wrapPayload` (which itself imports nothing), so
 * no node:zlib leaks into the browser bundle. See lib/payload-encode.ts.
 */

import { wrapPayload } from '@/lib/payload-encode'

export interface ShellFormInfo {
  form_oid: string
  form_name: string
  crf_text?: string
}

export interface ShellEvent {
  oid: string
  name: string
  [key: string]: unknown
}

/** The backend's success shape for POST /api/parallel-build/shell. */
export interface StudyShellResult {
  shell_xml: string
  study_name: string
  events: ShellEvent[]
  forms: ShellFormInfo[]
}

export interface GenerateShellRequest {
  sessionId: string
  protocolText: string
  forms: ShellFormInfo[]
}

/**
 * A shell failure that still knows WHY (task 3.6). The backend's typed
 * `code` — `shell_missing` above all — survives to the UI instead of
 * collapsing into a bare status number.
 */
export class ShellGenerationError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'ShellGenerationError'
    this.code = code
    this.status = status
  }
}

export async function generateStudyShell(
  request: GenerateShellRequest,
): Promise<StudyShellResult> {
  const response = await fetch('/api/parallel-build/shell', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Envelope, not raw JSON. This body is the most WAF-hostile in the whole
    // Study Designer flow: protocol_text is verbatim protocol prose ON TOP OF
    // the same forms[].crf_text that already 403'd Shay Kaden's QuickWave
    // build on /plan (SQLI 942110/949110, AnomalyScore 30 vs a threshold of
    // 5, 2026-08-17). Comparable prose bodies were measured tripping
    // SQLI-942410 at an 88% block rate. See lib/payload-encode.ts.
    body: JSON.stringify(
      wrapPayload({
        session_id: request.sessionId,
        protocol_text: request.protocolText,
        forms: request.forms,
      }),
    ),
  })

  if (!response.ok) {
    let code = `http_${response.status}`
    let message = `Shell generation failed (HTTP ${response.status})`
    try {
      const body = (await response.json()) as { code?: unknown; message?: unknown; detail?: unknown }
      // FastAPI serialises HTTPException(detail={...}) as {"detail": {...}},
      // so a typed backend error arrives NESTED, not top-level. Reading only
      // body.code degraded every one of them to http_<status> and threw away
      // the thing task 3.6 exists to preserve.
      const nested =
        body?.detail && typeof body.detail === 'object' && !Array.isArray(body.detail)
          ? (body.detail as { code?: unknown; message?: unknown })
          : null

      for (const candidate of [body?.code, nested?.code]) {
        if (typeof candidate === 'string' && candidate) code = candidate
      }

      const detail = [body?.message, nested?.message, body?.detail].find(
        (v): v is string => typeof v === 'string' && v.length > 0,
      )
      if (detail) message = detail
    } catch {
      // Non-JSON body (a WAF block page, an HTML 502). Keep the http_<status>
      // code rather than pretending we know the cause.
    }
    throw new ShellGenerationError(code, message, response.status)
  }

  const result = (await response.json()) as StudyShellResult

  // A 200 with no shell is the silent path this whole phase exists to close:
  // it would be written to the xml tab as-is and only surface minutes later
  // as "XML content is empty", pointing at the merge rather than at here.
  if (typeof result?.shell_xml !== 'string' || !result.shell_xml.trim()) {
    throw new ShellGenerationError(
      'shell_missing',
      'The shell worker returned no XML.',
      response.status,
    )
  }

  return result
}
